import { useMutation, useQuery } from '@tanstack/react-query';
import uniq from 'lodash/uniq';
import { useEffect, useRef } from 'react';
import {
  getMappingResults,
  getPartnerGamesMappingStats,
  getPromotedPartnerGames,
  getUnmappedPartnerGames,
  triggerMappingResults,
  updatePartnerGames,
} from 'src/api/cms';
import { useGetPlatformsData } from 'src/hooks/data/platforms';
import { showMappingResultsNotification } from 'src/services/notify/notify';
import { queryClient } from 'src/services/react-query/query-client';
import { usePartnerGamesStore } from 'src/stores/partner-games';
import type { GetCatalogGameMappingsResponse } from 'src/types/catalog-games';
import type {
  GetPromotedPartnerGamesBody,
  GetUnmappedPartnerGamesQuery,
  GetUnmappedPartnerGamesResponse,
  PromotedPartnerGamesTransformed,
} from 'src/types/partner-games';
import { useShallow } from 'zustand/shallow';

const usePartnerGamesState = usePartnerGamesStore.getState();

export const usePartnerGamesFilters = () =>
  usePartnerGamesStore(
    useShallow((state) => ({
      promotedMappedGamesFilters: state.promotedMappedGamesFilters,
      promotedUnmappedGamesFilters: state.promotedUnmappedGamesFilters,
    })),
  );

export const usePartnerGamesActions = () => ({
  setPromotedMappedGamesFilters: usePartnerGamesState.setPromotedMappedGamesFilters,
  setPromotedUnmappedGamesFilters: usePartnerGamesState.setPromotedUnmappedGamesFilters,
  setInitialPromotedMappedGamesFilters: usePartnerGamesState.setInitialPromotedMappedGamesFilters,
  setInitialPromotedUnmappedGamesFilters: usePartnerGamesState.setInitialPromotedUnmappedGamesFilters,
});

export const useGetPartnerGamesMappingStatsData = () => {
  const { data, isPending } = useQuery({
    queryKey: ['partnerGames', 'mappingStats'],
    queryFn: () => getPartnerGamesMappingStats(),
  });
  const { data: platforms, isInProgress: isPlatformsPending } = useGetPlatformsData();
  const allBrands = platforms?.flatMap((platform) => platform.brands);

  return {
    data: data
      ?.map((mappingStat) => {
        const foundBrand = allBrands?.find(
          (brand) =>
            brand.zingPlatformName === mappingStat.zingPlatformName &&
            brand.externalBrandName === mappingStat.externalBrandName,
        );

        return {
          ...mappingStat,
          mappingPercent: Math.min(mappingStat.mappedBetsCount / mappingStat.allBetsCount, 1),
          priority: foundBrand?.priority || 'normal',
          isNew: foundBrand?.isNew || false,
          cmsStatus: foundBrand?.cmsStatus || 'inactive',
        };
      })
      .filter((item) => item.cmsStatus !== 'inactive')
      .sort((a, b) => a.mappingPercent - b.mappingPercent),
    isInProgress: isPending || isPlatformsPending,
  };
};

export const useGetUnmappedPartnerGamesData = (payload: GetUnmappedPartnerGamesQuery) => {
  const { data, isPending } = useQuery({
    queryKey: ['partnerGames', 'unmapped', payload],
    queryFn: () => getUnmappedPartnerGames(payload),
  });

  return {
    data,
    isInProgress: isPending,
  };
};

export const useGetMappingResultsData = () => {
  const prevStatusRef = useRef<string | null>(null);

  const { data, isPending } = useQuery({
    queryKey: ['partnerGames', 'mappingResults'],
    queryFn: getMappingResults,
    refetchInterval: (data) => {
      if (!data) {
        return false;
      }

      return data.state.data?.status === 'running' ? 15000 : false;
    },
  });

  useEffect(() => {
    if (!data?.status) {
      return;
    }

    if (prevStatusRef.current === 'running' && data.status === 'not running') {
      showMappingResultsNotification(data);
    }

    prevStatusRef.current = data.status;
  }, [data]);

  return {
    data,
    isInProgress: isPending,
  };
};

// catalogGameId is the mappings list currently rendered by the caller: it must survive the update, otherwise the
// caller loses the rows it is showing while the user still works with them
export const useUpdatePartnerGamesAction = ({ catalogGameId }: { catalogGameId?: number } = {}) => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: updatePartnerGames,
    onSuccess: (updatedGames) => {
      const mappedIds = updatedGames.map(({ id }) => id);

      queryClient.setQueriesData<GetUnmappedPartnerGamesResponse>({ queryKey: ['partnerGames', 'unmapped'] }, (games) =>
        games?.filter((game) => !mappedIds.includes(game.id)),
      );

      queryClient.setQueriesData<PromotedPartnerGamesTransformed>(
        { queryKey: ['partnerGames', 'promoted', 'unmapped'] },
        (games) => games?.filter((game) => !mappedIds.includes(game.id)),
      );

      queryClient.setQueriesData<GetCatalogGameMappingsResponse>(
        {
          queryKey: ['catalogGames', 'mappings'],
          predicate: (query) => query.queryKey[2] !== catalogGameId,
        },
        (mappings) => mappings?.filter((mapping) => !mappedIds.includes(mapping.id)),
      );

      const targetCatalogGameIds = uniq(
        updatedGames.map(({ dimCsoSlotcatalogGmInfoId }) => dimCsoSlotcatalogGmInfoId),
      ).filter((id) => id !== null && id !== catalogGameId);

      targetCatalogGameIds.forEach((id) => {
        queryClient.removeQueries({ queryKey: ['catalogGames', 'mappings', id], exact: true });
      });
    },
  });

  return {
    updatePartnerGames: mutateAsync,
    isInProgress: isPending,
  };
};

export const useTriggerMappingResultsAction = () => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: triggerMappingResults,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['partnerGames', 'mappingResults'] }),
  });

  return {
    triggerMappingResults: mutateAsync,
    isInProgress: isPending,
  };
};

export const useGetPromotedPartnerGames = ({ type, brands }: GetPromotedPartnerGamesBody) => {
  const { data, isPending } = useQuery({
    queryKey: ['partnerGames', 'promoted', type, brands],
    queryFn: () => getPromotedPartnerGames({ type, brands }),
  });
  const { data: platforms, isInProgress: isPlatformsInProgress } = useGetPlatformsData({ withInactive: true });
  const allBrands = platforms?.flatMap((platform) => platform.brands);

  return {
    data: data?.filter((game) => {
      const foundBrand = allBrands?.find(
        (brand) =>
          brand.zingPlatformName === game.zingPlatformName && brand.externalBrandName === game.externalBrandName,
      );

      return foundBrand?.cmsStatus !== 'inactive';
    }),
    isInProgress: isPending || isPlatformsInProgress,
  };
};
